import { useTranslation } from "react-i18next";

import type { DeParaColumn } from "@/api/configuration-server";

/**
 * Target first: the consultant reads "what this becomes in Public Edition"
 * before "where it came from in ECC". Each band gets its own row background
 * (plain for target, a faint gold tint for source, fainter still for
 * description) and its own value tone — navy bold for target, gold-brown
 * bold for source, plain muted gray for description — matching the imported
 * design. The legend label itself stays a uniform muted gray across all
 * three bands; only the row tint and the value tone change.
 */
const BAND = {
  fieldTarget: { bg: "bg-[#fbfcfd]", value: "text-[#08254c]" },
  fieldSource: { bg: "bg-[rgba(255,200,61,0.08)]", value: "text-[#8a6100]" },
  descSource: { bg: "bg-[rgba(255,200,61,0.04)]", value: "text-[#596b85]" },
} as const;

const LEGEND_LABEL_COLOR = "text-[#596b85]";

const LEGEND_CELL =
  "sticky left-0 z-10 w-[180px] min-w-[180px] max-w-[180px] " +
  "border-r border-[#e8ebf0] px-3 py-2.5 text-left font-mono text-[10px] " +
  "uppercase tracking-[0.1em]";

// A legenda fica com fundo opaco fixo, diferente do mock: ela é sticky (rola
// por cima de muitos campos extraídos), e o mock não tem esse cenário de
// scroll horizontal na demo dele. Sem um fundo sólido aqui, o valor por
// baixo apareceria através da coluna fixa ao rolar.
const VALUES_LEGEND = "bg-[#f2f4f7] text-[#b2bccb]";
const RECORD_VALUE_COLOR = "text-[#2c3e5b]";

export function DeParaRecordsTable({
  columns,
  records,
}: {
  columns: DeParaColumn[];
  records: Array<Array<string | null>>;
}) {
  const { t } = useTranslation();

  return (
    <div className="max-h-[60vh] overflow-auto rounded-md border border-[#e8ebf0]">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr>
            <th
              data-testid="legend-field-target"
              className={`${LEGEND_CELL} ${LEGEND_LABEL_COLOR} ${BAND.fieldTarget.bg}`}
            >
              {t("Field (Target)")}
            </th>
            {columns.map((column, index) => (
              <th
                key={`ft-${column.field_name}`}
                data-testid={`target-field-${index}`}
                className={`whitespace-nowrap px-3 py-2.5 text-left font-mono font-semibold ${BAND.fieldTarget.bg} ${BAND.fieldTarget.value}`}
              >
                {column.target_field ?? "—"}
              </th>
            ))}
          </tr>
          <tr>
            <th
              data-testid="legend-field-source"
              className={`${LEGEND_CELL} ${LEGEND_LABEL_COLOR} ${BAND.fieldSource.bg}`}
            >
              {t("Field (Source)")}
            </th>
            {columns.map((column) => (
              <th
                key={`fs-${column.field_name}`}
                className={`whitespace-nowrap px-3 py-2.5 text-left font-mono font-semibold ${BAND.fieldSource.bg} ${BAND.fieldSource.value}`}
              >
                {column.field_name}
              </th>
            ))}
          </tr>
          <tr>
            <th
              data-testid="legend-desc-source"
              className={`${LEGEND_CELL} ${LEGEND_LABEL_COLOR} ${BAND.descSource.bg}`}
            >
              {t("Description (Source)")}
            </th>
            {columns.map((column) => (
              <th
                key={`ds-${column.field_name}`}
                className={`px-3 py-2.5 text-left font-normal ${BAND.descSource.bg} ${BAND.descSource.value}`}
              >
                {column.source_description ?? "—"}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((record, index) => (
            <tr
              key={index}
              data-testid={`record-row-${index}`}
              className={`border-t border-[#f2f4f7] ${index % 2 === 1 ? "bg-[#fbfcfd]" : ""}`}
            >
              <th
                data-testid={index === 0 ? "legend-values" : undefined}
                className={`${LEGEND_CELL} ${VALUES_LEGEND}`}
              >
                {index === 0 ? t("Values") : ""}
              </th>
              {record.map((value, cellIndex) => (
                <td
                  key={cellIndex}
                  className={`whitespace-nowrap px-3 py-2.5 font-mono ${RECORD_VALUE_COLOR}`}
                >
                  {value ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
